import React, { useState } from "react";
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  MenuItem,
  Divider,
  InputAdornment,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Chip,
  TablePagination,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { NotificationManager } from "react-notifications";
import { useNavigate } from "react-router-dom";
import { SaveBottleOrder } from "../../services/BottleOrdersService";
import ClientSelectorDialog from "../../../vlp/components/common/ClientSelectorDialog";
import BottleOrderSelectorDialog from "../common/BottleOrderSelectorDialog";
import { calculateScheduledOrderCount } from "./bottleOrderFormHelpers";

const ScheduleBottleOrder = () => {
  const navigate = useNavigate();

  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getEndOfYearDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    return `${year}-12-31`;
  };

  const [formData, setFormData] = useState({
    accountNumber: "",
    startDateTime: getCurrentDateTime(),
    endDateTime: getEndOfYearDateTime(),
    scheduleFrequency: "Once",
    sourceOrderNumber: null,
  });

  const [loading, setLoading] = useState(false);
  const [createdOrders, setCreatedOrders] = useState([]);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [bottleOrderDialogOpen, setBottleOrderDialogOpen] = useState(false);
  const [selectedBottleOrder, setSelectedBottleOrder] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const scheduleOptions = [
    { value: "Once", label: "Once" },
    { value: "Weekly", label: "Weekly" },
    { value: "Monthly", label: "Monthly" },
    { value: "Quarterly", label: "Quarterly" },
    { value: "Biannual", label: "Biannual" },
    { value: "Yearly", label: "Yearly" },
  ];

  // Handle client selection from dialog
  const handleClientSelect = (client) => {
    if (client) {
      setSelectedClient(client);
      setFormData((prev) => ({
        ...prev,
        accountNumber: client.accountNumber,
      }));
    }
  };

  // Handle bottle order selection from dialog
  const handleBottleOrderSelect = (order) => {
    if (order) {
      setSelectedBottleOrder(order);
      setFormData((prev) => ({
        ...prev,
        sourceOrderNumber: order.orderNumber,
      }));
    }
  };

  const calculateOrderCount = () =>
    calculateScheduledOrderCount(
      formData.startDateTime,
      formData.endDateTime,
      formData.scheduleFrequency,
      formData.sourceOrderNumber != null
    );

  const isFormValid = () => {
    return (
      formData.accountNumber.trim() !== "" &&
      formData.startDateTime !== "" &&
      formData.endDateTime !== "" &&
      formData.scheduleFrequency !== ""
    );
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const validateForm = () => {
    if (!formData.accountNumber.trim()) {
      NotificationManager.error("Account Number is required.");
      return false;
    }

    if (!formData.startDateTime) {
      NotificationManager.error("Start Date is required.");
      return false;
    }

    if (!formData.endDateTime) {
      NotificationManager.error("End Date is required.");
      return false;
    }

    const startDate = new Date(formData.startDateTime);
    const endDate = new Date(formData.endDateTime);

    if (endDate <= startDate) {
      NotificationManager.error("End Date must be after Start Date.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        accountNumber: formData.accountNumber,
        startDateTime: formData.startDateTime,
        endDateTime: formData.endDateTime,
        scheduleFrequency: formData.scheduleFrequency,
        sourceOrderNumber: formData.sourceOrderNumber,
      };

      const response = await SaveBottleOrder(payload);

      if (response && response.results) {
        NotificationManager.success(response.message);
        setCreatedOrders(response.results.createdOrders || []);
      } else {
        NotificationManager.error(
          response?.message || "Failed to schedule orders"
        );
      }
    } catch (error) {
      NotificationManager.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to schedule bottle order."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    navigate("/vlp/bottle-orders");
  };

  const handleCreateNewOrder = () => {
    setCreatedOrders([]);
    setFormData({
      accountNumber: formData.accountNumber, // Keep account number
      startDateTime: getCurrentDateTime(),
      endDateTime: getEndOfYearDateTime(),
      scheduleFrequency: "Once",
      sourceOrderNumber: null,
    });
    setSelectedBottleOrder(null);
  };

  return (
    <>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography
          variant="h5"
          sx={{ mb: 3, fontWeight: 600, color: "primary.main" }}
        >
          Schedule Bottle Order
        </Typography>

        {createdOrders.length > 0 ? (
          // Results View
          <Box>
            <Box
              sx={{
                mb: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "success.main" }}
              >
                Orders Created Successfully!
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCreateNewOrder}
              >
                Create Another Order
              </Button>
            </Box>

            <Paper>
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <TableContainer sx={{ maxHeight: 440, overflowY: "auto" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "primary.light" }}>
                        <TableCell sx={{ fontWeight: 600, color: "white" }}>
                          Order Number
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "white" }}>
                          Client
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "white" }}>
                          Account Number
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "white" }}>
                          Due Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "white" }}>
                          Order Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "white" }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "white" }}>
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {createdOrders
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((order) => (
                          <TableRow
                            key={order.orderNumber}
                            hover
                            sx={{
                              "&:nth-of-type(odd)": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                              },
                            }}
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {order.orderNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {order.clientName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {order.accountNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {order.dueDate ? new Date(order.dueDate).toLocaleDateString() : ""}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : ""}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={order.shippingStatus}
                                size="small"
                                color={
                                  order.shippingStatus === "Pending"
                                    ? "warning"
                                    : "default"
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <a
                                href={`/vlp/bottle-orders/edit/${order.orderNumber}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  textDecoration: "none",
                                  color: "inherit",
                                }}
                              >
                                Edit Order
                                <OpenInNewIcon fontSize="small" />
                              </a>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={createdOrders.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>

            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {createdOrders.length} order(s) created successfully
              </Typography>
            </Box>
          </Box>
        ) : (
          // Form View
          <Box component="form" onSubmit={handleSubmit}>
            {/* Account Information */}
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              Account Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Account Number"
                  value={
                    selectedClient
                      ? `${formData.accountNumber.trim()} - ${selectedClient.clientName.trim()}`
                      : "Click to select client"
                  }
                  onClick={() => setClientDialogOpen(true)}
                  InputProps={{
                    readOnly: true,
                    style: { cursor: "pointer" },
                    startAdornment: selectedClient ? null : (
                      <InputAdornment position="start">
                        <BusinessIcon color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: selectedClient ? (
                      <InputAdornment position="end">
                        <EditIcon color="primary" sx={{ fontSize: 20 }} />
                      </InputAdornment>
                    ) : null,
                  }}
                  helperText={
                    selectedClient
                      ? "Click to change client"
                      : "Click to select a client"
                  }
                  required
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Schedule Information */}
            <Typography
              variant="subtitle1"
              sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              Schedule Information
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Tooltip
                  title={
                    !selectedClient
                      ? "Please select a client first"
                      : "Click to select an existing bottle order to copy all ordered bottles from that order"
                  }
                  arrow
                  placement="top"
                >
                  <span>
                    <TextField
                      fullWidth
                      size="small"
                      label="Source Bottle Order (optional)"
                      value={
                        selectedBottleOrder
                          ? `${selectedBottleOrder.orderNumber} - ${selectedBottleOrder.clientName}`
                          : ""
                      }
                      onClick={() => selectedClient && setBottleOrderDialogOpen(true)}
                      placeholder="Click to select order"
                      variant="outlined"
                      disabled={!selectedClient}
                      InputProps={{
                        readOnly: true,
                        style: { cursor: selectedClient ? "pointer" : "not-allowed" },
                        endAdornment: selectedBottleOrder ? (
                          <InputAdornment position="end">
                            <EditIcon color="primary" sx={{ fontSize: 20 }} />
                          </InputAdornment>
                        ) : null,
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </span>
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Start Date"
                  type="date"
                  value={formData.startDateTime}
                  onChange={handleChange("startDateTime")}
                  variant="outlined"
                  required
                  disabled={!selectedClient}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="End Date"
                  type="date"
                  value={formData.endDateTime}
                  onChange={handleChange("endDateTime")}
                  variant="outlined"
                  required
                  disabled={!selectedClient}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Schedule Frequency"
                  select
                  value={formData.scheduleFrequency}
                  onChange={handleChange("scheduleFrequency")}
                  variant="outlined"
                  required
                  disabled={!selectedClient}
                >
                  {scheduleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            {/* Order Summary */}
            {isFormValid() && calculateOrderCount() > 0 && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: "info.lighter",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "info.light",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "info.main", mb: 1 }}
                >
                  Order Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Schedule Frequency
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formData.scheduleFrequency}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Start Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formData.startDateTime ? new Date(formData.startDateTime).toLocaleDateString() : ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      End Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formData.endDateTime ? new Date(formData.endDateTime).toLocaleDateString() : ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Orders
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "primary.main" }}
                    >
                      {calculateOrderCount()}{" "}
                      {calculateOrderCount() === 1 ? "Order" : "Orders"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancelClick}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || !isFormValid()}
              >
                {loading ? "Scheduling..." : "Schedule Order"}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Client Selector Dialog */}
      <ClientSelectorDialog
        open={clientDialogOpen}
        onClose={() => setClientDialogOpen(false)}
        onSelect={handleClientSelect}
        multiSelect={false}
      />

      {/* Bottle Order Selector Dialog */}
      <BottleOrderSelectorDialog
        open={bottleOrderDialogOpen}
        onClose={() => setBottleOrderDialogOpen(false)}
        onSelect={handleBottleOrderSelect}
        accountNumber={formData.accountNumber}
      />
    </>
  );
};

export default ScheduleBottleOrder;